import { checkIsHomeStay, getThumbnailUrl } from 'shared_logic/utils';
import * as GuestChatIcons from 'assets/modules/guest-chat';
import {
  getFormattedDate,
  getDateDifference,
  getStartOfDay,
} from '../../utilities/DateUtils';
import {
  capitalizeString,
  clearQueryParams,
  isNullOrUndefined,
} from 'utilities/Utils';
import {
  getMappedRTBStatus,
  domains,
  GIA_MYRA_CHAT_TID,
  RTBTimeFormat,
  dateDisplayFormat,
  GIA_MYRA_CHAT_STATUS,
  fullDomainNames,
  segments,
  UNIFIED_STATUSES,
  TID_INFO_MAP,
  INQUIRY_STATUS_LIST,
  RED_STATUS_LIST,
  GREEN_STATUS_LIST,
  PRIMARY_STATUS_LIST,
  PREV_BOOKING_RED_STATUS,
  PREV_BOOKING_GREEN_STATUS,
  PREV_BOOKING_PRIMARY_STATUS,
} from './GuestChatConstants';
import { goibiboIcon, makeMyTripIcon } from 'assets/common';
import { actualDomainNames } from 'shared_logic/constants';

export function getIsPreChat(bookingStatus) {
  return (
    bookingStatus.toLowerCase() !== UNIFIED_STATUSES.OLD_RTB_CONFIRMED &&
    bookingStatus !== UNIFIED_STATUSES.RTB_CONFIRMED &&
    bookingStatus !== UNIFIED_STATUSES.RTB_ACCEPTED
  );
}

export function isITBCancelled(isInstant, status) {
  return isInstant === 'true' && status === UNIFIED_STATUSES.RTB_CANCELLED;
}

export function getChatStatus(
  isPreChat,
  isRTB,
  isPreApproved,
  bookingStatus,
  STRINGS,
) {
  if (isPreChat) {
    if (
      isRTB ||
      isPreApproved ||
      bookingStatus === UNIFIED_STATUSES.RTB_WITHDRAWN ||
      bookingStatus === UNIFIED_STATUSES.RTB_CANCELLED
    ) {
      return {
        status: bookingStatus,
        statusText: getMappedRTBStatus(STRINGS)[bookingStatus],
      };
    }
    return {
      status: STRINGS.PRE_BKG_INQUIRY_STATUS,
      statusText: STRINGS.PRE_BKG_INQUIRY_STATUS,
    };
  } else if (
    bookingStatus === UNIFIED_STATUSES.RTB_CONFIRMED ||
    bookingStatus.toLowerCase() === UNIFIED_STATUSES.OLD_RTB_CONFIRMED
  ) {
    return {
      status: bookingStatus,
      statusText: STRINGS.CONFIRMED_STATUS,
    };
  }
  return { status: bookingStatus, statusText: bookingStatus };
}

export function getExpiryTime(time) {
  if (!time) return false;

  let str = 'in ';
  let dateDiff = getDateDifference(time, new Date());
  const hourDiff = getDateDifference(time, new Date(), 'hours');
  const minutesDiff = getDateDifference(time, new Date(), 'minutes');
  const secsDiff = getDateDifference(time, new Date(), 'seconds');

  if (secsDiff < 0) return false;

  if (dateDiff > 0) {
    str += dateDiff === 1 ? '1 day ' : `${dateDiff} days`;
    return str;
  }
  dateDiff = 0;

  const actualHourDiff = hourDiff < 24 ? hourDiff : hourDiff - dateDiff * 24;
  if (actualHourDiff > 0) {
    str += actualHourDiff === 1 ? '1 hour ' : `${actualHourDiff} hours `;
    return str;
  }
  const actualMinutesDiff =
    minutesDiff < 60 ? minutesDiff : minutesDiff - actualHourDiff * 60;
  if (minutesDiff > 0) {
    str += actualMinutesDiff === 1 ? '1 min' : `${actualMinutesDiff} mins`;
    return str;
  }
  const actualSecsDiff =
    secsDiff < 60 ? secsDiff : secsDiff - actualMinutesDiff * 60;
  if (secsDiff > 0) {
    str += actualSecsDiff === 1 ? '1 sec' : `${actualSecsDiff} secs`;
    return str;
  }
}

export const getIsMessageRead = (receiverReadTime, time) => {
  if (!receiverReadTime) {
    return false;
  }
  const receiverRead =
    receiverReadTime && new Date(receiverReadTime.seconds * 1000);
  const messageTimeParse = time && new Date(time.seconds * 1000);
  const diff = receiverRead.getTime() - messageTimeParse.getTime();
  if (diff >= 0) {
    return true;
  }
  return false;
};

export const getBreakDownObj = (priceObj, STRINGS) => {
  const payAtHotel = priceObj.payathotelflag;
  let vendor = priceObj.booking_vendor_name;
  if (vendor && vendor.toLowerCase() === 'makemytrip') {
    vendor = domains.MMT;
  }

  const N_STRINGS = STRINGS.DETAILS_DRAWER.PRICE_BREAKDOWN;
  const { SUM1: A, SUM2: B, SUM3: C, SUM4: D } = N_STRINGS;
  const VENDOR_TO_PAY_HOTEL = N_STRINGS.VENDOR_TO_PAY_HOTEL.replace(
    '__vendor__',
    vendor,
  );
  const AMT_HOTEL_PAY_VENDOR = N_STRINGS.AMT_HOTEL_PAY_VENDOR.replace(
    '__vendor__',
    vendor,
  );
  const AMT_VENDOR_PAY_HOTEL = N_STRINGS.AMT_HOTEL_PAY_VENDOR.replace(
    '__vendor__',
    vendor,
  );

  const breakDownList = [
    {
      text: N_STRINGS.ROOM_CHARGES,
      isBold: false,
      value: 0,
      key: 'total_breakup.total_base_price',
    },
    {
      text: N_STRINGS.EXTRA_ADULTS_CHILD,
      isBold: false,
      value: 0,
      key: 'total_breakup.total_extra_guest_price',
    },
  ];

  if (
    payAtHotel ||
    priceObj.is_gstn_registered ||
    priceObj.is_international_hotel
  ) {
    let text;
    if (priceObj.hotelnewbreakup.tax_pah) {
      text = N_STRINGS.HOTEL_TAXES_DESC;
    } else {
      text = N_STRINGS.HOTEL_TAXES;
    }
    breakDownList.push({
      text,
      value: 0,
      isBold: false,
      key: 'total_breakup.total_tax_exclude_service_charge',
    });
  }

  if (
    payAtHotel ||
    priceObj.is_gstn_registered ||
    priceObj.is_international_hotel
  ) {
    if (priceObj.service_charge) {
      const serviceCharge = priceObj.hotelnewbreakup.service_charge.percent;
      let text;
      if (priceObj.hotelnewbreakup.tax_pah) {
        text = `${N_STRINGS.SERVICE_CHARGE.replace(
          '__serviceCharge__',
          serviceCharge,
        )}`;
      } else {
        text = `${N_STRINGS.SERVICE_CHARGE_DESC.replace(
          '__serviceCharge__',
          serviceCharge,
        )}`;
      }
      breakDownList.push({
        text,
        value: 0,
        isBold: false,
        key: 'hotelnewbreakup.service_charge.amount',
      });
    }
  }

  if (!priceObj.gst) {
    breakDownList.push({
      text: N_STRINGS.HOTEL_DISCOUNT,
      value: 0,
      isBold: false,
      key: 'hotelnewbreakup.totalDiscountOnSell',
    });
  }

  breakDownList.push({
    text: `(${A}) ${N_STRINGS.PROPERTY_GROSS_CHARGES}`,
    value: 0,
    isBold: true,
    key: 'total_breakup.total_gross_charges_breakup',
  });

  breakDownList.push({
    text: `${vendor} ${N_STRINGS.COMMISION}`,
    value: 0,
    isBold: false,
    key: 'total_breakup.total_commission_exclude_service_tax_breakup',
  });

  breakDownList.push({
    text: priceObj.gst
      ? `${vendor} ${N_STRINGS.GST.replace('__gst__', priceObj.total_gst_rate)}`
      : `${vendor} ${N_STRINGS.SERVICE_TAX.replace('__serviceCharge__', 15)}`,
    value: 0,
    isBold: false,
    key: 'total_service_tax',
  });

  breakDownList.push({
    text: priceObj.gst
      ? `(${B}) ${vendor} ${N_STRINGS.COMMISION} (${N_STRINGS.INCLUDES_GST})`
      : `(${B}) ${vendor} ${N_STRINGS.COMMISION} (${N_STRINGS.INCLUDES_SERVICE_TAX})`,
    value: 0,
    isBold: true,
    key: 'total_breakup.total_commission_breakup',
  });

  // specific conditions for payNow & payAtHotel
  if (payAtHotel) {
    breakDownList.push({
      text: `(${C}) ${N_STRINGS.ADDITIONAL_CHARGES}`,
      value: 0,
      isBold: true,
      key: 'vendor_additional_charges',
    });
    breakDownList.push({
      text: `(${D}) ${N_STRINGS.CUSTOMER_PAYMENT_DESC1.replace(
        '__vendor__',
        vendor,
      )}`,
      value: 0,
      isBold: true,
      key: 'paid_at_site_amount',
    });
    breakDownList.push({
      text: `${N_STRINGS.CUSTOMER_PAYMENT_DESC2} (${A}+${C}-${D})`,
      value: 0,
      isBold: true,
      isColored: true,
      key: 'to_be_paid_at_hotel',
    });

    if (priceObj.gross_pay) {
      if (priceObj.paid_at_site_amount !== 0) {
        breakDownList.push({
          text: `${AMT_VENDOR_PAY_HOTEL} (${D})`,
          value: 0,
          isBold: true,
          isColored: true,
          key: 'paid_at_site_amount',
        });
      }
      breakDownList.push({
        text: `${AMT_HOTEL_PAY_VENDOR} (${B}+${C})`,
        value: 0,
        isBold: true,
        isColored: true,
        key: 'gross_return',
      });
    } else if (priceObj.margin >= 0) {
      breakDownList.push({
        text: `${AMT_HOTEL_PAY_VENDOR} (${B}+${C}-${D})`,
        value: 0,
        isBold: true,
        isColored: true,
        key: 'margin',
      });
    } else {
      breakDownList.push({
        text: `${AMT_VENDOR_PAY_HOTEL} (${D}-${B}-${C})`,
        value: 0,
        isBold: true,
        isColored: true,
        key: 'margin',
      });
    }
  } else {
    if (priceObj.tcs_amount) {
      breakDownList.push({
        text: `(${C}) ${N_STRINGS.TCS.replace(
          '__tcs__',
          priceObj.tcs_percentage,
        )}`,
        value: 0,
        isBold: true,
        key: 'tcs_amount',
      });
    }

    if (priceObj.gross_pay) {
      breakDownList.push({
        text: priceObj.tcs_amount
          ? `${VENDOR_TO_PAY_HOTEL} (${A} - ${C})`
          : `${VENDOR_TO_PAY_HOTEL} (${A})`,
        value: 0,
        isBold: true,
        isColored: true,
        key: 'total_breakup.goibibo_pay_to_hotel',
      });

      breakDownList.push({
        text: `${N_STRINGS.HOTEL_PAY_VENDOR.replace(
          '__vendor__',
          vendor,
        )} (${B})`,
        value: 0,
        isBold: true,
        isColored: true,
        key: 'total_breakup.hotel_pay_to_goibibo',
      });
    } else {
      breakDownList.push({
        text: priceObj.tcs_amount
          ? `${VENDOR_TO_PAY_HOTEL} (${A} - ${B} - ${C})`
          : `${VENDOR_TO_PAY_HOTEL} (${A} - ${B})`,

        value: 0,
        isBold: true,
        isColored: true,
        key: 'total_breakup.goibibo_pay_to_hotel',
      });
    }
  }
  return breakDownList;
};

export const populateBookingVoucherDetails = (
  bookingVoucherDetails,
  bookingDetailsArray,
) => {
  bookingDetailsArray.forEach(booking => {
    bookingVoucherDetails.forEach(item => {
      const keys = item.key.split('.');
      let value = booking[keys[0]];
      for (let i = 1; i < keys.length; i++) {
        if (value) {
          value = value[keys[i]];
        }
      }
      if (Number(value)) {
        item.value += Number(value.toFixed(2));
      }
    });
  });
};

export const parseResponse = res => {
  const roomsArray = [];
  res.requestToBooking.roomList.forEach(room => {
    const ratePlanArray = [];
    room.ratePlanList.forEach(ratePlan => {
      const ratePlanObj = {
        label: ratePlan.mealPlan,
        value: ratePlan.ratePlanCode,
        ...ratePlan,
      };
      ratePlanArray.push(ratePlanObj);
    });
    const roomObj = {
      label: room.roomTypeName,
      value: room.roomTypeCode,
      ratePlanList: ratePlanArray,
    };
    roomsArray.push(roomObj);
  });
  return roomsArray;
};

export const getValidVendorBookingId = booking => {
  if (booking.vendorBookingId) {
    return booking.vendorBookingId ?? '';
  }
  return booking.proBookingId ?? '';
};

export const generateITBPayload = (
  sessionContextKeys,
  roomObj,
  mealPlanValue,
  roomValue,
) => {
  const {
    platform,
    customerUserId,
    infoAttr: { hotelcode: hotelCode },
  } = sessionContextKeys;
  const { checkinValue, checkoutValue, roomsList } = roomObj;
  const paxItems = [];
  roomsList.forEach((room, ind) => {
    const ages = [];
    Object.keys(room.childAge).forEach(key => {
      ages.push(room.childAge[key]);
    });
    const newObj = {
      id: ind + 1,
      adults: room.adult,
      children: room.child,
    };
    if (room.child > 0) {
      newObj.childrenAges = ages;
    }
    paxItems.push(newObj);
  });

  const payload = {
    sellingVendor: platform,
    hotelCode,
    checkin: checkinValue,
    checkout: checkoutValue,
    userId: customerUserId,
    rooms: [
      {
        ratePlanCode: mealPlanValue.ratePlanCode,
        roomCode: roomValue.value,
        roomName: roomValue.label,
        paxItems: paxItems,
      },
    ],
  };
  return payload;
};

export const parseRoomsConfig = configString => {
  const config = configString.replace(/e/g, '');
  const roomsArray = [];
  for (let i = 0; i < config.length; i++) {
    const roomObj = {};
    roomObj.adults = parseInt(config[i]);
    roomObj.children = parseInt(config[i + 1]);
    const childrenAges = [];
    let j;
    for (j = i + 2; j < i + 2 + parseInt(config[i + 1]); j++) {
      childrenAges.push(parseInt(config[j]));
    }
    roomObj.childrenAges = childrenAges;
    roomsArray.push(roomObj);
    i = j - 1;
  }
  return roomsArray;
};

export const isBookingNotITBRelated = (isInquiry, bookingDetails) => {
  return (
    !isInquiry &&
    !!bookingDetails &&
    bookingDetails.status !== 'invite_expired' &&
    bookingDetails.status !== 'host_initiated' &&
    bookingDetails.status !== 'invite_to_book'
  );
};

export const fetchValuesFromLink = deepLink => {
  const roomsArray = parseRoomsConfig(
    clearQueryParams(
      deepLink.pathname,
      deepLink.search,
      'roomStayQualifier',
      true,
    ),
  );
  const roomsListLink = [];
  let totalAdults = 0;
  let totalChildren = 0;
  roomsArray.forEach((room, idx) => {
    const newObj = {};
    totalAdults = totalAdults + room.adults;
    totalChildren = totalChildren + room.children;
    newObj.adult = room.adults;
    newObj.child = room.children;
    if (room.childrenAges.length > 0) {
      const childObj = {};
      room.childrenAges.forEach((age, id) => {
        childObj[id + 1] = age;
      });
      newObj.childAge = childObj;
    } else {
      newObj.childAge = {};
    }
    if (idx < 1) {
      newObj.isInEdit = true;
    } else {
      newObj.isInEdit = false;
    }
    roomsListLink.push(newObj);
  });
  const totalRooms = roomsArray.length;

  return [roomsListLink, totalAdults, totalChildren, totalRooms];
};

export const fetchRoomValue = (roomsOptions, roomTypeCode) => {
  return roomsOptions.find(room => room.value === roomTypeCode);
};

export const fetchMealPlanValue = (mealPlanOptions, ratePlanCode) => {
  return mealPlanOptions.find(
    mealPlan => mealPlan.ratePlanCode === ratePlanCode,
  );
};

export const getChangesEventLabel = (
  refState,
  changedState,
  roomValue,
  mealPlanValue,
) => {
  const roomObj = refState.roomObj;
  const roomRef = refState.roomValue;
  const mealPlanRef = refState.mealPlanValue;
  const changedValues = new Set();
  if (roomObj.checkinValue !== changedState.checkinValue) {
    changedValues.add('checkin');
  }
  if (roomObj.checkoutValue !== changedState.checkoutValue) {
    changedValues.add('checkout');
  }

  for (let i = 0; i < roomObj.roomsList.length; i++) {
    if (
      roomObj?.adultValue !== changedState?.adultValue ||
      roomObj?.childValue !== changedState?.childValue
    ) {
      changedValues.add('pax');
    }
  }
  if (roomRef?.value && roomValue?.value && roomRef.value !== roomValue.value) {
    changedValues.add('roomtype');
  }
  if (mealPlanRef.ratePlanCode !== mealPlanValue.ratePlanCode) {
    changedValues.add('rateplan');
  }
  return [...changedValues].join('|');
};

export function getLastChat(STRINGS, data) {
  const cardMetaMap = TID_INFO_MAP(STRINGS);
  let currentCard = cardMetaMap[data.lastTidValue];
  const ITBCancelled = isITBCancelled(data.isInstant, data.status);
  if (ITBCancelled) {
    currentCard = cardMetaMap.itbCancelledCard;
  }
  const cardMSG = currentCard?.cardMsg
    .replace('__name__', data.guestName)
    .replace(
      '__expiryTime__',
      getFormattedDate(new Date(data.rtbExpiryTime), RTBTimeFormat),
    )
    .replace(
      '__checkin__',
      getFormattedDate(new Date(data.checkin), dateDisplayFormat),
    )
    .replace(
      '__checkout__',
      getFormattedDate(new Date(data.checkout), dateDisplayFormat),
    )
    .replace('__bkgAmt__', `${data.currency} ${data.bookingAmount}`)
    .replace('__reason__', data.rejectionReason);

  switch (data.lastTidValue) {
    case GIA_MYRA_CHAT_TID.RTB_AWAITING:
    case GIA_MYRA_CHAT_TID.RTB_CONFIRMED:
    case GIA_MYRA_CHAT_TID.RTB_REJECTED:
    case GIA_MYRA_CHAT_TID.ITB_SENT:
    case GIA_MYRA_CHAT_TID.RTB_FAILED:
    case GIA_MYRA_CHAT_TID.RTB_EXPIRED:
    case GIA_MYRA_CHAT_TID.ITB_EXPIRED:
    case GIA_MYRA_CHAT_TID.ITB_WITHDRAWN:
      return cardMSG;
    case GIA_MYRA_CHAT_TID.RTB_WITHDRAWN:
      if (ITBCancelled) {
        return cardMSG;
      }
      return data.lastChat;

    default:
      return data.lastChat;
  }
}

export const toTitleCase = phrase => {
  return phrase
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getStatusColor = status => {
  if (INQUIRY_STATUS_LIST.includes(status)) {
    return '#9B9B9B';
  } else if (RED_STATUS_LIST.includes(status)) {
    return '#EB2026';
  } else if (GREEN_STATUS_LIST.includes(status)) {
    return '#37BC8E';
  } else if (PRIMARY_STATUS_LIST.includes(status)) {
    return '#F2A526';
  }
};

export const getStatusColorForBooking = status => {
  if (PREV_BOOKING_RED_STATUS.includes(status)) {
    return '#EB2026';
  } else if (PREV_BOOKING_GREEN_STATUS.includes(status)) {
    return '#37BC8E';
  } else if (PREV_BOOKING_PRIMARY_STATUS.includes(status)) {
    return '#F2A526';
  }
  return '#9B9B9B';
};

export const getStatusText = status => {
  switch (status) {
    case 'ENQUIRY':
      return 'ENQUIRY RECEIVED';
    case 'AWAITING HOST CONFIRMATION':
      return 'BOOKING REQUEST';
    case 'PRE-APPROVED BY HOST':
      return 'INVITE TO BOOK';
    default:
      return status;
  }
};

export const getCTAsForStatus = (STRINGS, bookingStatus) => {
  let ctasForStatus = {};

  if (bookingStatus === UNIFIED_STATUSES.RTB_AWAITING) {
    ctasForStatus = {
      positiveCTA: STRINGS.DETAILS_DRAWER.CTAS.APPROVE,
      negativeCTA: STRINGS.DETAILS_DRAWER.CTAS.DECLINE,
    };
  } else if (bookingStatus === UNIFIED_STATUSES.ITB_SENT) {
    ctasForStatus = {
      positiveCTA: STRINGS.DETAILS_DRAWER.CTAS.EDIT_INVITE,
      negativeCTA: STRINGS.DETAILS_DRAWER.CTAS.WITHDRAW_INVITE,
    };
  } else if (
    [
      STRINGS.PRE_BKG_INQUIRY_STATUS,
      UNIFIED_STATUSES.ITB_WITHDRAWN,
      UNIFIED_STATUSES.RTB_WITHDRAWN,
      UNIFIED_STATUSES.RTB_EXPIRED,
      UNIFIED_STATUSES.RTB_REJECTED,
      UNIFIED_STATUSES.ITB_EXPIRED,
      UNIFIED_STATUSES.RTB_FAILED,
    ].indexOf(bookingStatus) > -1
  ) {
    ctasForStatus = {
      positiveCTA: STRINGS.DETAILS_DRAWER.CTAS.SHARE_PRE_APPROVED,
      negativeCTA: null,
    };
  } else if (
    bookingStatus.toLowerCase() === UNIFIED_STATUSES.OLD_RTB_CONFIRMED ||
    bookingStatus === UNIFIED_STATUSES.RTB_CONFIRMED ||
    bookingStatus === UNIFIED_STATUSES.RTB_ACCEPTED
  ) {
    ctasForStatus = {
      positiveCTA: null,
      negativeCTA: null,
    };
  }

  return ctasForStatus;
};

export function parseCustomerName(nameOfCustomer) {
  if (isNullOrUndefined(nameOfCustomer) || nameOfCustomer.trim() === '') {
    return 'Guest';
  }
  return capitalizeString(nameOfCustomer.toLowerCase());
}

export const getHotelName = (hotelCode, allHotelsList) => {
  const hotel = allHotelsList.filter(hotel => hotel.hotelcode === hotelCode)[0];
  return hotel?.hotel_name;
};

export function parseBreakup(breakup) {
  let parsedBreakup = {};
  if (breakup) {
    parsedBreakup = {
      payToHotel: breakup.nettAmount,
      grossPrice: breakup.propertyGrossCharges,
      commission: breakup.totalOTACommission + breakup.otaGSTAmount,
      discount: 0,
      tax: breakup.totalTaxCharges,
    };
  }
  return parsedBreakup;
}

export const getHotelThumbnail = (allHotelsList, hotelCode) => {
  const sessionHotel = allHotelsList.filter(
    hotel => hotel.hotelcode === hotelCode,
  );
  if (sessionHotel && sessionHotel.length > 0) {
    const isHomeStay = checkIsHomeStay(sessionHotel[0]);
    return getThumbnailUrl(sessionHotel[0].thumbnail_url, isHomeStay);
  }
  return getThumbnailUrl(null, false);
};

export function extractKeysFromSession({
  sessionData,
  STRINGS,
  isRTBEnabled,
  propertyName,
}) {
  if (!sessionData) return null;

  const { infoAttr, entityKey, nameOfCustomer, custUUD, ingoInfoAttr } =
    sessionData;
  if (isNullOrUndefined(infoAttr.status)) {
    infoAttr.status = UNIFIED_STATUSES.ENQUIRY;
  }

  const isPreChat = getIsPreChat(infoAttr.status);
  const platformLogo =
    infoAttr.platform === fullDomainNames.MMT ? makeMyTripIcon : goibiboIcon;
  const isMMTBlack = infoAttr.mmtBlack === 'true';
  const isGoTribe = infoAttr.goTribe === 'true';
  const segment = isMMTBlack
    ? segments.MMT_BLACK
    : isGoTribe
      ? segments.GO_TRIBE
      : 'all';
  const userSegmentLogo = isMMTBlack
    ? GuestChatIcons.MMTBlackLarge
    : isGoTribe
      ? GuestChatIcons.GoTribeLarge
      : null;
  let platform = infoAttr.platform ? infoAttr.platform : domains.MMT;
  if (platform && platform.toLowerCase() === 'makemytrip') {
    platform = domains.MMT;
  }
  const platformText =
    infoAttr.platform === fullDomainNames.MMT
      ? actualDomainNames.MMT
      : infoAttr.platform;
  const isRTB = infoAttr.isRtb === 'true';
  const isPreApproved = infoAttr.isPreApproved === 'true';
  const noOfAdult = Number(infoAttr.adults ?? 0);
  const customerImageUrl = sessionData.customerImageUrl;
  const noOfChild = Number(infoAttr.child ?? 0);
  const statusObj = getChatStatus(
    isPreChat,
    isRTB,
    isPreApproved,
    infoAttr.status,
    STRINGS,
  );
  const bookingStatus = statusObj.status;
  const stayDuration = getDateDifference(infoAttr.checkout, infoAttr.checkin);
  let showRTR = false;
  const isConversationClosed =
    sessionData?.chatStatus === GIA_MYRA_CHAT_STATUS.CLOSED ?? false;
  const isConversationMerged = !!sessionData?.merged ?? false;
  const isStatusConfirmed =
    infoAttr.status === UNIFIED_STATUSES.RTB_CONFIRMED ||
    infoAttr.status.toLowerCase() === UNIFIED_STATUSES.OLD_RTB_CONFIRMED;
  const showFixedBanner =
    isRTBEnabled &&
    [
      UNIFIED_STATUSES.RTB_ACCEPTED,
      UNIFIED_STATUSES.RTB_WITHDRAWN,
      UNIFIED_STATUSES.RTB_CANCELLED,
    ].indexOf(bookingStatus) === -1 &&
    !isStatusConfirmed;

  if (
    isStatusConfirmed &&
    getDateDifference(infoAttr.checkin, getStartOfDay(new Date())) <= 0
  ) {
    // Show RTR(Request To Review) when checkin date is elapsed, postbooking, confirmed booking
    showRTR = true;
  }
  return {
    hotelCode: infoAttr.hotelcode,
    sessionData,
    infoAttr,
    ITB_PRICE_BREAKUP: ingoInfoAttr?.ITB_PRICE_BREAKUP ?? {},
    isPreChat,
    platformLogo,
    userSegmentLogo,
    platform,
    platformText,
    checkInDate: getFormattedDate(infoAttr.checkin, dateDisplayFormat),
    checkOutDate: getFormattedDate(infoAttr.checkout, dateDisplayFormat),
    bkgDate: getFormattedDate(infoAttr.bookingDate, dateDisplayFormat),
    bookingStatus,
    bookingStatusText: statusObj.statusText,
    nameOfCustomer: parseCustomerName(nameOfCustomer),
    entityKey,
    isMMTBlack,
    isGoTribe,
    isRTB,
    isPreApproved,
    provisionalBkgId: infoAttr.funnelBookingID,
    segment,
    customerUserId: custUUD,
    stayDuration,
    showRTR,
    propertyName: propertyName,
    noOfAdult,
    noOfChild,
    roomCode: infoAttr.roomTypeCode,
    noOfGuests: noOfAdult + noOfChild,
    noOfRooms: Number(infoAttr.roomCount),
    showFixedBanner,
    isConversationClosed,
    isConversationMerged,
    isRTBEnabled,
    customerImageUrl,
    bookingAmount: infoAttr.bookingAmount,
  };
}
